/**
 * WorkspaceTabBar — 5-tab navigation with per-stage StatusChips.
 *
 * Tabs: Intake | Data | Analysis | Deliverables | Settlement
 * Each tab shows a StatusChip with representative state.
 * Active indicator uses var(--color-brand) (#FF6600).
 * Status chips: orange for pending/warning, var(--color-done) for completed.
 */

import React from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { StatusBadge } from '@/components/shared/StatusBadge';

export type WorkspaceTab = 'intake' | 'data' | 'analysis' | 'deliverables' | 'settlement';

interface TabConfig {
  id: WorkspaceTab;
  label: string;
  statusText: string;
  statusVariant: 'warning' | 'success' | 'neutral' | 'pending';
}

const TABS: TabConfig[] = [
  { id: 'intake', label: 'Intake', statusText: 'Attest pending', statusVariant: 'warning' },
  { id: 'data', label: 'Data', statusText: '7 anomalies', statusVariant: 'warning' },
  { id: 'analysis', label: 'Analysis', statusText: '12/20 queries', statusVariant: 'pending' },
  { id: 'deliverables', label: 'Deliverables', statusText: '3 ready', statusVariant: 'success' },
  { id: 'settlement', label: 'Settlement', statusText: 'In progress', statusVariant: 'neutral' },
];

interface WorkspaceTabBarProps {
  /** Currently active tab */
  activeTab: WorkspaceTab;
  /** Base path for navigation (e.g., /case/:id) */
  basePath: string;
  /** Optional className */
  className?: string;
}

export function WorkspaceTabBar({ activeTab, basePath, className = '' }: WorkspaceTabBarProps) {
  const navigate = useNavigate();

  const handleTabClick = (tabId: WorkspaceTab) => {
    if (tabId === activeTab) return;
    navigate(`${basePath}/${tabId}`);
  };

  return (
    <nav
      className={`bg-[var(--color-bg-surface)] border-b border-[var(--color-border)] ${className}`}
      data-testid="workspace-tab-bar"
    >
      <div className="flex px-2">
        {TABS.map((tab) => {
          const isActive = tab.id === activeTab;
          return (
            <button
              key={tab.id}
              onClick={() => handleTabClick(tab.id)}
              className={`
                relative flex items-center gap-2 px-4 py-3 text-sm font-medium
                transition-colors duration-150
                ${isActive
                  ? 'text-[var(--color-brand)]'
                  : 'text-[var(--color-text-secondary)] hover:text-[var(--color-text-primary)] hover:bg-[var(--color-bg-hover)]'
                }
              `}
              data-testid={`tab-${tab.id}`}
              aria-selected={isActive}
              role="tab"
            >
              <span>{tab.label}</span>
              <StatusBadge
                text={tab.statusText}
                variant={tab.statusVariant}
                size="sm"
              />
              {isActive && (
                <span
                  className="absolute bottom-0 left-0 right-0 h-0.5 bg-[var(--color-brand)]"
                  data-testid="active-tab-indicator"
                />
              )}
            </button>
          );
        })}
      </div>
    </nav>
  );
}

export default WorkspaceTabBar;
